import React, {useCallback, useEffect, useState} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import './SecurityGroupBottom.scss';
import {useUpdateAnalyticStatus} from '../../../hooks/securityGroup/useSecurityGroups';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';

type SelectedOption = 'projectLevel' | 'securityGroupSpecific';

interface SecuritygroupBottomProps {
  onSave: (isSecurityGroupSpecific: boolean) => void;
  isModified?: boolean;
  isSaveEnabled?: boolean;
  currentSecurityGroupId: number | null;
  initialSelectedOption: SelectedOption;
  isDataLoading?: boolean;
}

interface ToastState {
  open: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  id: string;
}

const radioOptionsData = [
  {
    label: t('securityGroup.projectLevel.label'),
    value: 'projectLevel',
    tooltip: t('securityGroup.projectLevel.infoTooltipText'),
  },
  {
    label: t('securityGroup.securityGroupSpecific.label'),
    value: 'securityGroupSpecific',
    tooltip: t('securityGroup.securityGroupSpecific.infoTooltipText'),
  },
];

const SecuritygroupBottom: React.FC<SecuritygroupBottomProps> = ({
  onSave,
  isModified,
  isSaveEnabled,
  currentSecurityGroupId,
  initialSelectedOption,
}) => {
  const [selectedOption, setSelectedOption] = useState<SelectedOption>(
    initialSelectedOption
  );
  const [hasUserMadeChange, setHasUserMadeChange] = useState(false);
  useEffect(() => {
    if (currentSecurityGroupId === null) {
      setSelectedOption('projectLevel');
    } else {
      setSelectedOption(initialSelectedOption);
    }
    setHasUserMadeChange(false);
  }, [initialSelectedOption, currentSecurityGroupId]);
  const [showRegenerationModal, setShowRegenerationModal] = useState(false);
  const [toastState, setToastState] = useState<ToastState>({
    open: false,
    type: 'success',
    title: '',
    message: '',
    id: 'initial',
  });

  const {mutate: mutateRegenerationStatus, isPending: isRegenerating} =
    useUpdateAnalyticStatus();

  const handleOptionChange = (value: string) => {
    const newOption = value as SelectedOption;
    setSelectedOption(newOption);
    if (newOption !== initialSelectedOption) {
      setHasUserMadeChange(true);
    } else {
      setHasUserMadeChange(false);
    }
  };
  const isActionEnabled = isModified || hasUserMadeChange;
  const isSaveActionEnabled = isSaveEnabled || hasUserMadeChange;

  const handleToastClose = useCallback(() => {
    setToastState((prev) => ({...prev, open: false}));
  }, []);
  const handleRefresh = () => {
    window.location.reload();
  };
  const handleOpenRegenerationModal = useCallback(() => {
    if (selectedOption === 'securityGroupSpecific') {
      setShowRegenerationModal(true);
    }
  }, [selectedOption]);

  const handleCloseRegenerationModal = useCallback(() => {
    setShowRegenerationModal(false);
  }, []);

  const handleConfirmRegeneration = () => {
    if (currentSecurityGroupId === null) {
      console.error('Cannot regenerate: Security Group ID is missing.');
      handleCloseRegenerationModal();
      return;
    }

    mutateRegenerationStatus(
      {
        securityGroupID: currentSecurityGroupId,
        isInprogress: true,
        isSecurityGroupLevel: true,
      },
      {
        onSuccess: () => {
          console.log('Regeneration API call succeeded!');
          setToastState({
            id: Date.now().toString(),
            open: true,
            type: 'success',
            title: t('securityGroup.toastMessages.successTitle'),
            message: t('securityGroup.toastMessages.successMessage'),
          });
        },
        onError: (error) => {
          console.error('Regeneration API call failed:', error);
          setToastState({
            id: Date.now().toString(),
            open: true,
            type: 'error',
            title: t('securityGroup.toastMessages.errorTitle'),
            message: t('securityGroup.toastMessages.errorMessage'),
          });
        },
      }
    );
    handleCloseRegenerationModal();
  };

  const handleRegenerationClick = () => {
    handleOpenRegenerationModal();
  };

  const isTooltipVisible = (optValue: SelectedOption | string) => {
    if (optValue === 'projectLevel') {
      return true;
    }

    if (optValue === 'securityGroupSpecific') {
      return selectedOption === 'securityGroupSpecific';
    }

    return false;
  };
  const regenerationModalButtons = [
    {
      id: 'cancel-regeneration',
      label: t('common.no'),
      onClick: handleCloseRegenerationModal,
      dataTestId: 'regen-cancel',
      variant: 'secondary' as const,
      disabled: isRegenerating,
    },
    {
      id: 'confirm-regeneration',
      label: t('common.yes'),
      onClick: handleConfirmRegeneration,
      dataTestId: 'regen-confirm',
      variant: 'primary' as const,
      disabled: isRegenerating,
    },
  ];

  return (
    <Box className="securitygroup-bottom-container">
      <Box className="radio-button-section">
        {radioOptionsData.map((opt) => (
          <Box key={opt.value} className="radio-option-group">
            <Box className="radio-option-row">
              <ArrRadio
                name="selectionType"
                value={selectedOption}
                onChange={handleOptionChange}
                data={[{label: opt.label, value: opt.value}]}
                className="single-radio-override radio-label-text"
                dataTestId={`radio-${opt.value}`}
              />

              {opt.tooltip && isTooltipVisible(opt.value) && (
                <ArrTooltipWrapper
                  title={t('securityGroup.securityGroupSpecific.title')}
                  trigger={
                    <InfoIcon
                      className="view-user-info-icon"
                      data-testid={`info-icon-${opt.value}`}
                    />
                  }
                >
                  {opt.tooltip}
                </ArrTooltipWrapper>
              )}
            </Box>

            {opt.value === 'securityGroupSpecific' &&
              selectedOption === 'securityGroupSpecific' && (
                <Box className="regeneration-action-row">
                  <ArrButton
                    variant="secondary"
                    size="small"
                    onClick={handleRegenerationClick}
                    dataTestId="run-regeneration-button"
                  >
                    {t('securityGroup.regenerationButton.label')}
                  </ArrButton>

                  <Typography
                    className="regeneration-info-text"
                    data-testid={`security-group-bottom-regeneration-info-text`}>
                    {t('securityGroup.regenerationButton.regenerationInfoText')}
                  </Typography>
                </Box>
              )}
          </Box>
        ))}
      </Box>
      <Divider
        orientation="horizontal"
        flexItem
        className="arr-divider security-group-divider"
      />
      <Box className="action-buttons-container">
        <ArrButton
          variant="secondary"
          title={t('common.cancel')}
          size="small"
          disabled={!isActionEnabled}
          onClick={handleRefresh}
          dataTestId="cancel-button"
          aria-label={t('common.cancel')}
          aria-live="polite"
          aria-atomic="false"
          aria-disabled={!isActionEnabled}
          aria-describedby={t('securityGroup.cancelButton.buttonTitle')}
        >
          {t('common.cancel')}
        </ArrButton>

        <ArrButton
          variant="primary"
          title={t('common.save')}
          size="small"
          disabled={!isSaveActionEnabled}
          onClick={() => {
            const isSecurityGroupSpecific =
              selectedOption === 'securityGroupSpecific';
            onSave(isSecurityGroupSpecific); // pass boolean to parent
          }}
          dataTestId="save-button"
          aria-label={t('common.save')}
          aria-live="polite"
          aria-atomic="false"
          aria-disabled={!isSaveActionEnabled}
          aria-describedby={t('securityGroup.saveButton.buttonTitle')}
        >
          {t('common.save')}
        </ArrButton>
      </Box>
      <ArrModal
        showModal={showRegenerationModal}
        title={t('securityGroup.regenerationModal.modalHeader')}
        showClose={true}
        autoFocusCloseButton={true}
        onClose={handleCloseRegenerationModal}
        dataTestId="regeneration-confirm-modal"
        buttons={regenerationModalButtons}
        className="regeneration-confirm-modal"
      >
        <Typography
          variant="body1"
          className="typo-text title-text-color"
          data-testid={`security-group-bottom-modal-msg`}>
          {t('securityGroup.regenerationModal.modalMsg')}
        </Typography>
      </ArrModal>
      {toastState.open && (
        <ArrToastNotification
          key={toastState.id}
          message={toastState.message}
          status={[toastState.type as ToastType]}
          onClose={handleToastClose}
        />
      )}
    </Box>
  );
};

export default SecuritygroupBottom;
